import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { cn, doEncryptLocal, loog } from '@express-labs/raven-tools';
import {
  Blurb,
  Button,
  IconV2,
  Link,
  Loader,
  LocationChanger,
  Title,
} from '@express-labs/raven-ui';
import StoreDetails from '../StoreDetails';
import s from './FindStore.module.scss';
import { analyze } from '../../../../../helpers';

function FindStore({
  customerStore,
  getNearByStores,
  heartBeatData,
  isLoggedIn,
  lineItems,
  nearByStores,
  onModalClose,
  preferredStore,
  setZipCode,
  storeLoading,
  updateStoreInCart,
  zipCode,
}) {
  const postalCode = customerStore?.postalCode;

  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(storeLoading);

  const toggleChangeLocation = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const onZipcodeChange = (zipcode) => {
    setZipCode(zipcode);
    getNearByStores({
      variables: {
        zipcode
      },
    });
  };

  const updateCustomerDataInStorage = (customerData) => {
    try {
      const hbeatStorage = localStorage?.getItem('exp_hbeat');
      if (hbeatStorage) {
        const hbeatData = JSON.parse(hbeatStorage);
        const { ct, iv, salt } = JSON.parse(
          doEncryptLocal.doEncrypt(
            `${hbeatData.anon ? 'A' : 'L'}${hbeatData.uid}`,
            JSON.stringify(customerData),
          ),
        );
        sessionStorage?.setItem('hbSession', `${ct}|${iv}|${salt}`);
      }
    } catch (e) {
      loog(
        'Error while update the store details in storage',
        'info',
        e.message,
      );
    }
  };

  const onSelectStore = useCallback(
    (e) => {
      const storeId = e.target.getAttribute('data-storeId');
      setLoading(true);
      const lineItemIds = lineItems?.map((item) => item.product.sku.skuId);
      analyze({
        name: 'changedStore',
        data: {
          storeId,
          url: window?.location?.href,
        },
        props: {},
      });
      return updateStoreInCart({
        variables: {
          storeId,
          items: lineItemIds,
        },
      })
        .then((response) => {
          const store =
            response?.updateStoreInfoForCart?.customerStoreInfo?.orderStore;
          setLoading(false);
          document.cookie = 'updateStoreSkipOnce=1;path=/;';
          updateCustomerDataInStorage({
            ...heartBeatData,
            customerStore: store,
          });
          window?.Heartbeat?.beat?.();
          onModalClose();
        })
        .catch((error) => {
          setLoading(false);
          loog(
            'Error while update the store details in cart',
            'info',
            error.message,
          );
        });
    },
    [heartBeatData, lineItems, onModalClose, updateStoreInCart],
  );

  const onSelectStoreKeyPress = (e) => {
    /* istanbul ignore else */
    if (e.key === 'Enter' || e.key === ' ') {
      onSelectStore(e);
    }
  };

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  return (
    <>
      <div className={cn(s.changeLocation, zipCode && s.hasZip)}>
        <div className={cn(s.headerContainer, s.headerBottom)}>
          {zipCode && (
            <span className={s.iconPin}>
              <IconV2 icon="locator" size="small" />
            </span>
          )}
          <Title tag="h4" className={s.locationHeader2}>
            {zipCode ? `Stores near ${zipCode}` : 'Enter location'}
          </Title>
          <Link
            as="button"
            theme="dark"
            size="body"
            aria-label="Change my location"
            onClick={toggleChangeLocation}
            aria-expanded={isOpen}
            aria-controls="location-changer-form"
            id="change-location-button"
            data-open={`${isOpen}`}
            className={s.changeLocationDropdown}
          >
            Change Location
            <span className={s.caret} role="presentation" />
          </Link>
        </div>
        <div
          key="location-changer-form"
          id="location-changer-form"
          className={s.locationChangeForm}
          aria-hidden={!isOpen}
          aria-labelledby="change-location-button"
        >
          {isOpen && (
            <LocationChanger
              key="location-changer"
              postalCode={postalCode}
              onLocationChange={onZipcodeChange}
              className={s.storeLocationChanger}
              alwaysEnabled
            />
          )}
        </div>
      </div>
      <div className={s.searchResultContainer}>
        {loading && (
          <div className={s.loader} aria-busy>
            <Loader
              data-loader="InventoryContainer"
              size="large"
              display="inline"
              chroma="dark"
            />
          </div>
        )}
        {nearByStores?.length
          ? nearByStores?.map((store) => (
            <div key={store?.storeNumber}>
                <StoreDetails
                  customerStore={store}
                  isLoggedIn={isLoggedIn}
                  onSelectStore={onSelectStore}
                  preferredStore={preferredStore}
                />
                <div className={s.shopStoreWrapper}>
                  <span className={s.shopStoreButtonContainer}>
                    <Button
                      as="button"
                      data-testid="pickup-button"
                      data-storeid={store?.storeNumber}
                      data-storenumber={store?.storeNumber}
                      theme="dark"
                      size="large"
                      onClick={onSelectStore}
                      onKeyPress={onSelectStoreKeyPress}
                      value="Shop the store"
                      className={cn(s.fullWidth, s.pickupButton)}
                    >
                      PICK UP HERE
                      <span className="srOnly">store details</span>
                    </Button>
                  </span>
                </div>
              </div>
            ))
          : !loading && (
              <div className={s.noItemsAvailable} role="alert">
                <Blurb tag="p" className="bodyError">
                  {zipCode
                    ? 'No stores found within 50 miles.'
                    : 'Please enter a postal code.'}
                </Blurb>
                {zipCode && nearByStores?.length === 0 && (
                  <Blurb tag="p" className="bodyPrimary">
                    Please enter a different postal code.
                  </Blurb>
                )}
              </div>
            )}
      </div>
    </>
  );
}

FindStore.propTypes = {
  customerStore: PropTypes.shape({
    storeId: PropTypes.string,
    postalCode: PropTypes.string,
  }),
  getNearByStores: PropTypes.func.isRequired,
  heartBeatData: PropTypes.shape({
    customerStore: PropTypes.shape({
      storeId: PropTypes.string,
      postalCode: PropTypes.string,
    }),
  }),
  isLoggedIn: PropTypes.bool,
  lineItems: PropTypes.arrayOf(PropTypes.shape({})),
  nearByStores: PropTypes.arrayOf(
    PropTypes.shape({
      storeId: PropTypes.string,
      storeNumber: PropTypes.string,
    }),
  ),
  onModalClose: PropTypes.func.isRequired,
  preferredStore: PropTypes.string,
  setZipCode: PropTypes.func.isRequired,
  storeLoading: PropTypes.bool,
  updateStoreInCart: PropTypes.func.isRequired,
  zipCode: PropTypes.string.isRequired
};

FindStore.defaultProps = {
  customerStore: {},
  heartBeatData: {},
  isLoggedIn: false,
  lineItems: [],
  nearByStores: [],
  preferredStore: null,
  storeLoading: null,
};

export default FindStore;
